import type { Client } from '@/types/client';
import type { DrvPrimaryType } from '@/lib/api/drv';
import { resolveManualNutritionTargets } from '@/pages/manual/nutritionalAnalysis/nutritionalAnalysisPure';
import { resolveDrvRecommendedValue } from '@/utils/drv-resolve-primary-target';
import { resolveClientTargetCalories } from '@/utils/client-goals';
import type { AnalysisData, KpiItem } from './analysis.shared';
import {
  extractClientGoalEnergyKcal,
  readClientGoalAmount,
  readClientGoalGramAmount,
  readClientGoalWaterLiters,
  safeNumber,
} from './analysis.shared';

function needsDrvFallback(target: number): boolean {
  return !Number.isFinite(target) || target <= 0;
}

const DRV_EXTRA_NUTRIENT_IDS: Array<[string, string[]]> = [
  ['free sugars', ['free_sugars', 'added_sugars', 'sugar']],
  ['saturated fat', ['saturated_fat']],
  ['trans fat', ['trans_fat']],
  ['alcohol', ['alcohol']],
  ['sodium', ['sodium']],
  ['monounsaturated fat', ['monounsaturated_fat', 'mufa', 'monounsaturated', 'monounsaturatedfat']],
  ['polyunsaturated fat', ['polyunsaturated_fat', 'pufa', 'polyunsaturated', 'polyunsaturatedfat']],
];

/**
 * KPI targets prefer profile EER + structured `clientGoals`. When a value is still invalid
 * (≤0 / non-finite), fill from DRV RDA/AI (`resolveManualNutritionTargets` / primary types),
 * or from the same numeric seeds used in manual analysis when no DRV object exists.
 */
export function applyDrvFallbackTargetsToAnalysisData(
  base: AnalysisData,
  client: Client,
  primaryTypes: DrvPrimaryType[] | undefined,
): AnalysisData {
  const needsAny =
    needsDrvFallback(base.energyTarget)
    || needsDrvFallback(base.proteinTarget)
    || needsDrvFallback(base.carbsTarget)
    || needsDrvFallback(base.fatTarget)
    || needsDrvFallback(base.fibreTarget)
    || needsDrvFallback(base.waterTarget)
    || base.kpis.some((k) => needsDrvFallback(k.target));

  if (!needsAny) return base;

  const hasDrv = client.drvRequirements != null && typeof client.drvRequirements === 'object';
  const eerKcal = safeNumber(resolveClientTargetCalories(client), 0);
  const goalEnergyKcal = extractClientGoalEnergyKcal(client.clientGoals);

  const drvCtx = {
    drvRequirements: hasDrv ? client.drvRequirements : undefined,
    primaryTypes,
    clientGoals: client.clientGoals,
    ...(eerKcal > 0 ? { eerKcal } : {}),
  };

  const resolved = resolveManualNutritionTargets(
    {
      calories: base.energyTarget > 0 ? base.energyTarget : 0,
      protein: base.proteinTarget > 0 ? base.proteinTarget : 0,
      carbs: base.carbsTarget > 0 ? base.carbsTarget : 0,
      fat: base.fatTarget > 0 ? base.fatTarget : 0,
    },
    drvCtx,
  );

  const proteinGoal = readClientGoalGramAmount(client.clientGoals, 'protein') ?? 0;
  const carbsGoal = readClientGoalGramAmount(client.clientGoals, 'carbohydrates') ?? 0;
  const fatGoal = readClientGoalGramAmount(client.clientGoals, 'totalFat') ?? 0;
  const fibreGoal = readClientGoalAmount(client.clientGoals, 'dietaryFibre', ['grams', 'value']) ?? 0;

  const pickWithGoalsFirst = (baseVal: number, goalVal: number, drvOrSeed: number): number => {
    if (!needsDrvFallback(baseVal)) return baseVal;
    if (goalVal > 0) return goalVal;
    return drvOrSeed;
  };

  const nextEnergyTarget = !needsDrvFallback(base.energyTarget)
    ? base.energyTarget
    : eerKcal > 0
      ? eerKcal
      : goalEnergyKcal > 0
        ? goalEnergyKcal
        : resolved.calories;

  const nextProteinTarget = pickWithGoalsFirst(base.proteinTarget, proteinGoal, resolved.protein);
  const nextCarbsTarget = pickWithGoalsFirst(base.carbsTarget, carbsGoal, resolved.carbs);
  const nextFatTarget = pickWithGoalsFirst(base.fatTarget, fatGoal, resolved.fat);
  const nextFibreTarget = pickWithGoalsFirst(base.fibreTarget, fibreGoal, resolved.fiber);

  let nextWaterTarget = base.waterTarget;
  const waterGoal = readClientGoalWaterLiters(client.clientGoals, safeNumber(client.weightKg, 0)) ?? 0;
  if (needsDrvFallback(nextWaterTarget) && waterGoal > 0) {
    nextWaterTarget = waterGoal;
  }
  if (needsDrvFallback(nextWaterTarget) && hasDrv) {
    const w = resolveDrvRecommendedValue(client.drvRequirements, primaryTypes, ['water', 'total_water'], 0);
    if (w > 0) nextWaterTarget = w;
  }

  const patchKpiTargets = (kpis: KpiItem[]): KpiItem[] =>
    kpis.map((k) => {
      if (!needsDrvFallback(k.target)) return k;
      const key = k.label.trim().toLowerCase();
      if (key === 'energy') return { ...k, target: nextEnergyTarget };
      if (key === 'protein') return { ...k, target: nextProteinTarget };
      if (key === 'carbs') return { ...k, target: nextCarbsTarget };
      if (key === 'fat') return { ...k, target: nextFatTarget };
      if (key === 'fibre' || key === 'fiber') return { ...k, target: nextFibreTarget };
      if (key === 'water') return { ...k, target: nextWaterTarget > 0 ? nextWaterTarget : k.target };

      const extra = DRV_EXTRA_NUTRIENT_IDS.find(([label]) => label === key);
      if (extra && hasDrv) {
        const v = resolveDrvRecommendedValue(client.drvRequirements, primaryTypes, extra[1], 0);
        if (v > 0) return { ...k, target: v };
      }
      return k;
    });

  return {
    ...base,
    energyTarget: nextEnergyTarget,
    proteinTarget: nextProteinTarget,
    carbsTarget: nextCarbsTarget,
    fatTarget: nextFatTarget,
    fibreTarget: nextFibreTarget,
    waterTarget: nextWaterTarget,
    kpis: patchKpiTargets(base.kpis),
  };
}
